import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SandboxLoginPage() {
  const { slot } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    axios
      .get(`${API}/sandbox/${slot}/config`)
      .then((r) => setConfig(r.data))
      .catch(() => setConfig({ slot, status: "available" }))
      .finally(() => setLoading(false));
  }, [slot]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const r = await axios.post(`${API}/sandbox/${slot}/login`, { email, password });
      // Sandbox session stored under a namespaced key so it doesn't collide
      // with the main-tenant login session
      localStorage.setItem(
        `sandbox_session_${slot}`,
        JSON.stringify({
          token: r.data.token,
          user: r.data.user,
          sandbox_id: r.data.sandbox_id,
          property_name: r.data.property_name,
          logo_url: r.data.logo_url,
        })
      );
      // Phase 2 will route to a fully-branded portal per role. For Phase 1 we
      // land on a stub page confirming successful sandbox login + property.
      navigate(`/sandbox/${slot}/dashboard`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || "Login failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-500" data-testid="sandbox-login-loading">Loading sandbox…</p>
      </div>
    );
  }

  const isAvailable = !config || config.status !== "active";

  if (isAvailable) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-10 text-center border border-slate-200">
          <div
            className="inline-block bg-amber-100 text-amber-800 border border-amber-300 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider mb-6"
            data-testid="sandbox-badge-unavailable"
          >
            SANDBOX ENVIRONMENT
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-3" data-testid="sandbox-unavailable-title">
            Session Expired
          </h1>
          <p className="text-slate-600 mb-6" data-testid="sandbox-unavailable-message">
            This sandbox slot is currently unavailable. Please contact your HERChain Solutions representative to configure a new session.
          </p>
          <p className="text-xs text-slate-400 mt-8">Powered by HERChain Solutions</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-10 border border-slate-200">
        {/* Amber sandbox badge */}
        <div className="text-center mb-6">
          <div
            className="inline-block bg-amber-100 text-amber-800 border border-amber-300 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider"
            data-testid="sandbox-badge"
          >
            SANDBOX ENVIRONMENT
          </div>
        </div>

        {/* Prospect logo (if uploaded) */}
        {config.logo_url && (
          <div className="flex justify-center mb-4">
            <img
              src={`${BACKEND_URL}${config.logo_url}`}
              alt={`${config.property_name} logo`}
              className="max-h-20 max-w-[220px] object-contain"
              data-testid="sandbox-property-logo"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          </div>
        )}

        {/* Prospect property name */}
        <h1
          className="text-3xl font-bold text-center text-slate-800 mb-2"
          data-testid="sandbox-property-name"
        >
          {config.property_name}
        </h1>
        <p className="text-center text-sm text-slate-500 mb-8">
          Procurement Sandbox — {config.tax_type} {config.tax_rate}%
        </p>

        {/* Login form */}
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="sandbox-login-form">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-400 focus:outline-none"
              placeholder="pm@..."
              required
              data-testid="sandbox-email-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-400 focus:outline-none"
              required
              data-testid="sandbox-password-input"
            />
          </div>
          {error && (
            <p className="text-sm text-red-600" data-testid="sandbox-login-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-slate-800 hover:bg-slate-900 text-white py-2.5 rounded-md font-semibold disabled:opacity-50"
            data-testid="sandbox-login-submit"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>

        {/* HERChain footer branding */}
        <p className="text-center text-xs text-slate-400 mt-10" data-testid="sandbox-hc-footer">
          Powered by <span className="font-semibold text-slate-500">HERChain Solutions</span>
        </p>
      </div>
    </div>
  );
}
